import React, { useMemo } from 'react';
import { Cell, CellProps, HeaderProps } from 'react-table';
import moment from 'moment/moment';

import { useI18n } from 'i18n';
import { ITepEntryPass } from 'types/tep';
import { SinceCell } from 'components/organisms/tep/tep-management/cells/since';
import { UntilCell } from 'components/organisms/tep/tep-management/cells/until';
import { EmailCell } from 'components/organisms/tep/tep-management/cells/email';
import { ActionsCell } from 'components/organisms/tep/tep-management/cells/actions';
import { PlateNumberCell } from 'components/organisms/tep/tep-management/cells/plate-number';
import { RowSelectCell } from 'components/organisms/tep/tep-management/cells/row-select-cell';
import { RowSelectHeader } from 'components/organisms/tep/tep-management/cells/row-select-header';

export const useTepColumns = () => {
  const { fc } = useI18n();

  const getDateCell = ({ cell }: CellProps<ITepEntryPass>) => (
    <>{cell.value ? moment(cell.value).format('YYYY-MM-DD') : '-'}</>
  );

  const getPlateNumberCell = ({ cell, row }: CellProps<ITepEntryPass>) => (
    <PlateNumberCell entryPass={cell.row.original} />
  );

  const getEmailCell = ({ cell }: CellProps<ITepEntryPass>) => <EmailCell entryPass={cell.row.original} />;

  const getSinceCell = ({ cell }: CellProps<ITepEntryPass>) => <SinceCell entryPass={cell.row.original} />;

  const getUntilCell = ({ cell }: CellProps<ITepEntryPass>) => <UntilCell entryPass={cell.row.original} />;

  const getStatusCell = ({ cell }: { cell: Cell<ITepEntryPass> }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const defaultColor = cell.row.isSelected ? '#ffffff' : '#777777';
    const color = cell.value === 'AVAILABLE' ? '#1C7903' : defaultColor;

    return <div style={{ color }}>{cell.value ? fc(cell.value.toLowerCase()) : '-'}</div>;
  };

  const getActionsCell = ({ cell }: { cell: Cell<ITepEntryPass> }) => (
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    <ActionsCell isSelected={cell.row.isSelected} entryPass={cell.row.original} />
  );

  return useMemo(
    () => [
      {
        id: 'selection',
        accessor: '',
        width: 60,
        minWidth: 60,
        isFlexUnset: true,
        sticky: 'left',
        Header: ({ rows }: HeaderProps<ITepEntryPass>) => <RowSelectHeader rows={rows} />,
        Cell: (cell: CellProps<ITepEntryPass>) => <RowSelectCell row={cell.row} />,
      },
      { Header: fc('Device Name'), accessor: 'name' },
      {
        Header: fc('Device IMEI'),
        accessor: 'device.imei',
        Cell: ({ cell }: CellProps<ITepEntryPass>) => cell.row.original.device.uri,
        width: 200,
        minWidth: 200,
      },
      {
        Header: fc('company'),
        accessor: 'lease.company.name',
        Cell: ({ cell }: CellProps<ITepEntryPass>) => cell.row.original.lease?.company || '-',
      },
      { Header: fc('email'), accessor: 'lease.email', Cell: getEmailCell, width: 250, minWidth: 250 },
      { Header: fc('Plate Number'), accessor: 'lease.plateNumber', Cell: getPlateNumberCell },
      { Header: fc('TEP Start'), accessor: 'lease.startOn', Cell: getSinceCell },
      { Header: fc('TEP End'), accessor: 'lease.endOn', Cell: getUntilCell },
      { Header: fc('Drafted At'), accessor: 'lease.draftedAt', Cell: getDateCell },
      { Header: fc('Issued At'), accessor: 'lease.issuedAt', Cell: getDateCell },
      // { Header: fc('Returned On'), accessor: 'lease.returnedOn', Cell: getDateCell },
      { Header: fc('status'), accessor: 'status', Cell: getStatusCell },
      {
        Header: fc('actions'),
        accessor: 'actions',
        width: 200,
        minWidth: 200,
        Cell: getActionsCell,
        isFlexUnset: true,
        sticky: 'right',
      },
    ],
    [],
  );
};
